/* ==========================================================================
   Satjeevan — Recommended Carousel JS (product-recommended-carousel.js)

   Responsibilities:
     1. Arrow-button navigation + boundary disabling + drag-to-scroll +
        keyboard navigation for the track (same technique as
        assets/product-fbt-carousel.js).
     2. Instant client-side "Size" variant switching per card (price,
        compare-at, sale badge, image, availability, hidden variant input)
        — same convention as assets/product-fbt-carousel.js and
        assets/sj-bestsellers.js.

   Unlike those two, all of the above is wired via EVENT DELEGATION on the
   track element itself (not on individual cards/chips). This section's
   <product-recommendations> wrapper can rewrite the track's contents via
   Horizon's morphSection() once real Shopify recommendations load — event
   delegation means none of this needs to be re-bound after that morph,
   since the listeners live on the track, which morphSection preserves.

   Adding to cart itself is handled entirely by Horizon's existing
   <product-form-component> / <add-to-cart-component> custom elements
   (assets/product-form.js, loaded globally) — this file never touches
   /cart/add.js directly, avoiding any duplicate cart logic.
   ========================================================================== */

(function () {
  'use strict';

  var INIT_ATTR = 'data-sj-prc-init';

  function applyVariant(card, variant, chipEl) {
    if (!variant) return;

    var priceEl = card.querySelector('[data-prc-price]');
    if (priceEl) priceEl.textContent = variant.price;

    var showCompare = Number(variant.compareRaw) > Number(variant.priceRaw);
    var compareEl = card.querySelector('[data-prc-compare]');
    if (compareEl) {
      if (showCompare) {
        compareEl.textContent = variant.compare;
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.setAttribute('hidden', '');
      }
    }

    var imageWrap = card.querySelector('.sj-prc-image-wrap');
    var badgeEl = card.querySelector('.sj-prc-badge');
    if (showCompare) {
      if (!badgeEl && imageWrap) {
        badgeEl = document.createElement('span');
        badgeEl.className = 'sj-prc-badge';
        imageWrap.insertBefore(badgeEl, imageWrap.firstChild);
      }
      if (badgeEl) badgeEl.textContent = variant.discountPercent + '% OFF';
    } else if (badgeEl) {
      badgeEl.remove();
    }

    var variantInput = card.querySelector('[data-prc-variant-input]');
    if (variantInput) variantInput.value = variant.id;

    var imageEl = card.querySelector('.sj-prc-image');
    if (imageEl && variant.image) {
      imageEl.src = variant.image;
      imageEl.removeAttribute('srcset');
    }

    var chips = card.querySelectorAll('[data-prc-chip]');
    chips.forEach(function (c) {
      var isSelected = c === chipEl;
      c.classList.toggle('sel', isSelected);
      c.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    var atcBtn = card.querySelector('.sj-prc-atc-btn');
    if (atcBtn) atcBtn.disabled = !variant.available;
  }

  function handleTrackClick(event, track) {
    var chip = event.target.closest('[data-prc-chip]');
    if (!chip) return;

    var card = chip.closest('[data-sj-prc-card]');
    if (!card) return;

    var variantsScript = card.querySelector('[data-prc-variants]');
    if (!variantsScript) return;

    var variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (err) {
      return;
    }

    var variantId = chip.getAttribute('data-variant-id');
    var variant = null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) {
        variant = variants[i];
        break;
      }
    }
    applyVariant(card, variant, chip);
  }

  function initTrack(track) {
    if (track.hasAttribute(INIT_ATTR)) return;
    track.setAttribute(INIT_ATTR, '1');

    var section = track.closest('.sj-prc-section');
    var prevBtn = section ? section.querySelector('[data-sj-prc-prev]') : null;
    var nextBtn = section ? section.querySelector('[data-sj-prc-next]') : null;

    function step() {
      var card = track.querySelector('.sj-prc-card');
      if (!card) return track.clientWidth * 0.8;
      var style = window.getComputedStyle(track);
      var gap = parseFloat(style.columnGap || style.gap || '18') || 18;
      return card.getBoundingClientRect().width + gap;
    }

    function updateBoundaryState() {
      var maxScroll = track.scrollWidth - track.clientWidth - 1;
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 0;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= maxScroll;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
    }

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        track.scrollBy({ left: step(), behavior: 'smooth' });
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      }
    });

    track.addEventListener('scroll', updateBoundaryState, { passive: true });
    window.addEventListener('resize', updateBoundaryState);
    updateBoundaryState();

    // Recompute boundary state once the track's contents are replaced by
    // Horizon's morphSection() (real recommendations arriving via AJAX).
    var morphObserver = new MutationObserver(function () {
      updateBoundaryState();
    });
    morphObserver.observe(track, { childList: true });

    /* Mouse drag-to-scroll (desktop only; touch gets native swipe) */
    var isDown = false;
    var startX = 0;
    var startScroll = 0;
    var moved = false;

    track.addEventListener('mousedown', function (e) {
      // Prevent the browser's native drag-ghost gesture (links/images) from
      // fighting the custom mousemove-based scroll below. Form controls are
      // excluded so button/chip clicks still work.
      if (!e.target.closest('input, textarea, select, button')) {
        e.preventDefault();
      }
      isDown = true;
      moved = false;
      track.classList.add('dragging');
      startX = e.pageX;
      startScroll = track.scrollLeft;
    });

    window.addEventListener('mouseup', function () {
      isDown = false;
      track.classList.remove('dragging');
    });

    window.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      e.preventDefault();
      var dx = e.pageX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startScroll - dx;
    });

    /* Event-delegated chip clicks + drag-gesture click suppression, both
       scoped to the track so they survive any future morph of its children. */
    track.addEventListener(
      'click',
      function (e) {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        handleTrackClick(e, track);
      },
      true
    );
  }

  /* Grid contexts (e.g. sections/collection-product-grid.liquid) reuse this
     same card + variant-swap logic but have nothing to drag/scroll/arrow-nav
     — just the chip click delegation, on the grid container itself. */
  function initGrid(grid) {
    if (grid.hasAttribute(INIT_ATTR)) return;
    grid.setAttribute(INIT_ATTR, '1');
    grid.addEventListener(
      'click',
      function (e) {
        handleTrackClick(e, grid);
      },
      true
    );
  }

  function init() {
    document.querySelectorAll('[data-sj-prc-track]').forEach(initTrack);
    document.querySelectorAll('[data-sj-prc-grid]').forEach(initGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor live-reload support
  document.addEventListener('shopify:section:load', init);
})();
